import Image from 'next/image';
import { memo, useCallback } from 'react';

import { useTrackOpenInMobileAppClicked } from '@air/analytics';
import { XIcon } from '@air/design-icons';

import airAppIcon from '~/assets/air-ios-app-icon.png';
import { useMobileAppBannerSessionStore } from '~/components/Banners/MobileAppBanner/hooks/useMobileAppBannerSessionStore';
import { useShouldShowMobileAppBanner } from '~/components/Banners/MobileAppBanner/hooks/useShouldShowMobileAppBanner';
import { useMobileHandoffUrl } from '~/hooks/useMobileHandoffUrl';

type MobileAppBannerProps = {
  workspaceId?: string;
};

export const MobileAppBanner = memo(({ workspaceId }: MobileAppBannerProps) => {
  const { shouldShow } = useShouldShowMobileAppBanner();
  const dismiss = useMobileAppBannerSessionStore((state) => state.dismiss);
  const { trackOpenInMobileAppClicked } = useTrackOpenInMobileAppClicked();
  const handoffUrl = useMobileHandoffUrl({ workspaceId });

  const handleView = useCallback(() => {
    trackOpenInMobileAppClicked({ location: 'banner' });
  }, [trackOpenInMobileAppClicked]);

  const canShowBanner = shouldShow && Boolean(handoffUrl);

  if (!canShowBanner || !handoffUrl) {
    return null;
  }

  return (
    <div
      className="flex w-full shrink-0 items-center gap-1 border-b ads:border-b-default border-b-gray-4 ads:bg-subtle bg-gray-3 py-4 pr-6 pl-2"
      data-testid="MOBILE_APP_BANNER"
    >
      <button
        aria-label="Dismiss"
        className="shrink-0 rounded-4 p-1 ads:text-icon-subtle text-gray-9"
        data-testid="MOBILE_APP_BANNER_CLOSE"
        onClick={dismiss}
        type="button"
      >
        <XIcon size="sm" />
      </button>

      <Image alt="" className="size-16 shrink-0 rounded-10" height={64} priority src={airAppIcon} width={64} />

      <div className="flex min-w-0 flex-1 flex-col justify-center pl-1">
        <p className="truncate font-medium ads:text-primary text-16 text-altitude-12">Air - Unlock Creative Flow</p>
        <p className="truncate font-medium ads:text-metadata text-12 text-gray-9">Air Labs, Inc.</p>
        <p className="truncate ads:text-metadata text-14 text-gray-9">GET — On the App Store</p>
      </div>

      {/* Routes through air.inc's /mobile/open handoff rather than app.air.inc's: a same-domain tap
          never triggers the mobile app's Universal Link, so the handoff must be cross-domain. */}
      <a
        className="shrink-0 rounded-4 p-1 font-medium ads:text-accent text-16 text-blue-7"
        data-testid="MOBILE_APP_BANNER_VIEW"
        href={handoffUrl}
        onClick={handleView}
      >
        View
      </a>
    </div>
  );
});

MobileAppBanner.displayName = 'MobileAppBanner';
